import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { BaseParams, DetailsParams } from '@interfaces/app/nav.interfaces';
import { CallOff } from '@interfaces/call-off/callOff';
import { DeliveryNote, OrderHeaderRow } from '@interfaces/order';
import { Observable, catchError, combineLatest, map, of, startWith, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';

import { fixCallOffRows, fixDeliveryNotes } from '@services/api.fixes';
import { flattenResponses, getById, getOffsetDate } from '@services/utils';

@Injectable({ providedIn: 'root' })
export class CallOffService {
  http = inject(HttpClient);

  getCallOffs(baseParams: BaseParams): Observable<CallOff[] | null> {
    const { customerId, sourceSystemCodes, dateFrom, dateTo } = baseParams;
    return combineLatest(
      sourceSystemCodes.map((sourceSystemCode) => {
        const url = `${environment.calloffApi}/customers/${customerId}/calloffs`;
        const params = {
          sourcesystemid: sourceSystemCode,
          deliverydatefrom: dateFrom,
          deliverydateto: dateTo,
          calloffstatuses: ['Unknown', 'Pending', 'Confirmed', 'Completed', 'Cancelled'],
        };

        return this.http.get<CallOff[]>(url, { params }).pipe(
          startWith(null),
          map((callOffs) => fixCallOffRows(callOffs)),
          catchError(() => of([] as CallOff[])),
        );
      }),
    ).pipe(map(flattenResponses));
  }

  getCallOffsWithPurchaseOrderNumbers(baseParams: BaseParams): Observable<CallOff[] | null> {
    return combineLatest([
      this.getCallOffs(baseParams),
      this.getPurchaseOrderNumberMap(baseParams),
    ]).pipe(
      map(([callOffs, purchaseOrderNumberMap]) => {
        if (!callOffs || !purchaseOrderNumberMap) {
          return null;
        }
        const updatedCalloffs = callOffs.map((callOff) => {
          if (!callOff.callOffRows) return callOff;

          callOff.callOffRows = callOff.callOffRows.map((row) => {
            if (!row.orderNumber) return row;
            row.purchaseOrderNumber = purchaseOrderNumberMap.get(row.orderNumber.toString());
            return row;
          });

          return callOff;
        });
        return updatedCalloffs;
      }),
    );
  }

  getCalloffById(detailsParams: DetailsParams): Observable<CallOff | null> {
    const { callOffNumber, sourceSystemCode } = detailsParams;
    return this.getCallOffsWithPurchaseOrderNumbers(detailsParams).pipe(
      map((callOffs) =>
        getById(callOffs || [], {
          callOffNumber: parseInt(callOffNumber),
          sourceSystemCode: parseInt(sourceSystemCode),
        }),
      ),
    );
  }

  getDeliveries(detailsParams: DetailsParams): Observable<DeliveryNote[] | null> {
    const { customerId, sourceSystemCode, orderNumber } = detailsParams;
    const url = `${environment.orderApi}/customers/${customerId}/deliveries`;
    const params = {
      ordernumber: orderNumber,
      sourcesystemid: Number(sourceSystemCode),
      dispatcheddatefrom: getOffsetDate(1000),
    };

    return this.http.get<DeliveryNote[]>(url, { params }).pipe(
      startWith(null),
      map((deliveries) => fixDeliveryNotes(deliveries)),
      catchError(() => of([] as DeliveryNote[])),
    );
  }

  getDeliveriesByCallOff(detailsParams: DetailsParams): Observable<any> {
    return this.getCalloffById(detailsParams).pipe(
      switchMap((callOff) => {
        if (!callOff || !callOff.callOffRows) {
          return of(null);
        }
        const orderNumbers: string[] = [
          ...new Set(callOff.callOffRows.map((row) => row.orderNumber?.toString())),
        ] as string[];

        return combineLatest(
          orderNumbers.map((orderNumber) => this.getDeliveries({ ...detailsParams, orderNumber })),
        ).pipe(
          map(flattenResponses),
          map((deliveries) =>
            deliveries?.filter((delivery) => delivery.callOffNumber === callOff.callOffNumber),
          ),
        );
      }),
    );
    // Extract order numbers from callOffRows and remove duplicates
  }

  getPurchaseOrderNumberMap(baseParams: BaseParams): Observable<Map<string, string>> {
    const { customerId, sourceSystemCodes } = baseParams;
    return combineLatest(
      sourceSystemCodes.map((sourceSystemCode) => {
        const url = `${environment.orderApi}/customers/${customerId}/orderrows`;
        const params = {
          sourcesystemid: sourceSystemCode,
          orderstatuses: [
            'Confirmed',
            'Pending',
            'New',
            'Produced',
            'Dispatched',
            'Delivered',
            'Cancelled',
          ],
          ordereddatefrom: getOffsetDate(720),
        };
        return this.http.get<OrderHeaderRow[]>(url, { params }).pipe(
          startWith(null),
          catchError(() => of([] as OrderHeaderRow[])),
        );
      }),
    ).pipe(
      map((responses: (OrderHeaderRow[] | null)[]) => {
        const purchaseOrderNumberMap = new Map<string, string>();
        responses.forEach((orderHeaderRows: OrderHeaderRow[] | null) => {
          if (!orderHeaderRows) return;
          orderHeaderRows.forEach((row: OrderHeaderRow) => {
            if (!row.orderNumber || !row.purchaseOrderNumber) return;
            purchaseOrderNumberMap.set(row.orderNumber, row.purchaseOrderNumber);
          });
        });

        return purchaseOrderNumberMap;
      }),
    );
  }
}
